import React, { useState } from 'react';
import CategorySelect from 'react/components/CategorySelect';
import Button from 'react/components/Button';
import TransactionBankInformation from 'react/components/TransactionBankInformation';
import CurrencyInput from 'react/elements/CurrencyInput';
import MaterialIcon from 'react/elements/MaterialIcon';
import TextInput from 'react/elements/TextInput';
import Transaction from 'models/Transaction';
import { bankName, dateHint, splitStatus } from './transactionEditHints';

/**
 * The transaction edit popup's contents, laid out like the meal idea editor: the name as the
 * heading, a labelled section per field, and a footer holding Delete and Save.
 *
 * The transaction itself is a mutable model that TransactionSummaryComponent owns, so every edit
 * goes back to it through the `on*` props and this only renders what it is given. The one piece of
 * state here is whether Delete is asking first.
 *
 * A new transaction (no id) picks its day within the month being viewed, as it always has; one that
 * exists takes any date.
 *
 * `CategoryPicker` is CategorySelect unless a test swaps it: react-select does not render under jsdom.
 */
export default function TransactionEditPanel({
	transaction,
	categories,
	categoryToDisplay,
	categoryLocked = false,
	isSaving = false,
	dateEditDay,
	firstInputRef,
	onChange,
	onBlur,
	onEnter,
	onAmountChange,
	onToggleSign,
	onCategories,
	onSplitAmount,
	onSplitChange,
	onSave,
	onDelete = null,
	onClose,
	CategoryPicker = CategorySelect,
}) {
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const isNew = !transaction.id;
	const isSplit = transaction.isSplit();
	const isExpense = transaction.isAnExpenseBasedOnAmountAndCategory();
	const fromBank = bankName(transaction);
	const when = dateHint(transaction.transaction_date);
	const splits = splitStatus(transaction);
	const where = transaction.where === null || transaction.where === undefined ? '' : transaction.where;
	const hasBank = !!transaction.getPlaidTransaction();

	const setSign = (expense) => {
		if(expense !== isExpense) {
			onToggleSign();
		}
	};

	return (
		<div className='transaction_edit_panel'>
			<div className='panel_heading'>
				<div className='transaction_edit_panel__heading-text'>
					<div className='input-field transaction_edit_panel__where'>
						<TextInput
							ref={firstInputRef}
							aria-label='Where'
							placeholder='Where was it?'
							name='where'
							value={where}
							disabled={!!isSaving}
							onChange={onChange}
							onBlur={onBlur}
							onKeyDown={onEnter}
							data-autofocus={isNew ? true : undefined}
						/>
					</div>
					{hasBank &&
					<div className='transaction_edit_panel__source'>
						<TransactionBankInformation transaction={transaction} showPending />
					</div>
					}
				</div>
				<button type='button' className='btn-flat transaction_edit_panel__icon' title='Close' aria-label='Close' onClick={onClose}>
					<MaterialIcon>close</MaterialIcon>
				</button>
			</div>

			<div className='transaction_edit_panel__body'>
				{!!fromBank &&
				<p className='transaction_edit_panel__hint transaction_edit_panel__bank-name'>
					Your bank calls it <span className='transaction_edit_panel__quoted'>{fromBank}</span>
				</p>
				}

				<div className='transaction_edit_panel__pair'>
					<section className='transaction_edit_panel__section transaction_edit_panel__amount'>
						<h6>Amount</h6>
						<div className='input-field'>
							<CurrencyInput
								aria-label='Amount'
								allowNegative={true}
								name='amount'
								value={transaction.amount}
								disabled={!!isSaving}
								onChange={onAmountChange}
								onKeyDown={onEnter}
							/>
						</div>
						<div className='transaction_edit_panel__sign' role='group' aria-label='Expense or income'>
							<button
								type='button'
								className={`transaction_edit_panel__sign-option is-expense ${isExpense ? 'is-selected' : ''}`}
								aria-pressed={isExpense}
								disabled={!!isSaving}
								onClick={() => setSign(true)}
							>
								Expense
							</button>
							<button
								type='button'
								className={`transaction_edit_panel__sign-option is-income ${isExpense ? '' : 'is-selected'}`}
								aria-pressed={!isExpense}
								disabled={!!isSaving}
								onClick={() => setSign(false)}
							>
								Income
							</button>
						</div>
					</section>

					<section className='transaction_edit_panel__section transaction_edit_panel__date'>
						<h6>Date</h6>
						{isNew ?
						<div className='transaction_edit_panel__day'>
							<span>{transaction.transaction_date.format('MMM')}</span>
							<div className='input-field'>
								<TextInput
									aria-label='Day'
									type='number'
									name='dateEditDay'
									value={dateEditDay}
									disabled={!!isSaving}
									onChange={onChange}
									onBlur={onBlur}
								/>
							</div>
							<span>{transaction.transaction_date.format('YYYY')}</span>
						</div> :
						<div className='input-field'>
							<input
								aria-label='Date'
								type='date'
								name='transaction_date'
								value={transaction.transaction_date ? transaction.transaction_date.format('YYYY-MM-DD') : ''}
								disabled={!!isSaving}
								onChange={onChange}
							/>
						</div>
						}
						{!!when && <p className='transaction_edit_panel__hint'>{when}</p>}
					</section>
				</div>

				<section className='transaction_edit_panel__section'>
					<h6>Category</h6>
					<CategoryPicker
						isMulti
						value={isSplit ? transaction.splits.map((split) => split.category_id) : categoryToDisplay}
						onChange={onCategories}
						disabled={categoryLocked || isSplit}
						categories={categories}
						blank={Transaction.getBlankTransactionCategoryDisplayText(transaction)}
						transactionName={transaction.where}
					/>
					<p className='transaction_edit_panel__hint'>{categoryHint(categoryLocked, isSplit)}</p>
				</section>

				{isSplit &&
				<section className='transaction_edit_panel__section'>
					<h6>Splits</h6>
					<ul className='transaction_edit_panel__splits'>
						{transaction.splits.map((split, index) => (
							<li key={split.id || `index-${index}`} className='transaction_edit_panel__split'>
								<div className='transaction_edit_panel__split-category'>
									<CategoryPicker
										value={split.category_id}
										onChange={(option) => onSplitChange(split, { name: 'category_id', ...option })}
										categories={categories}
										transactionName={transaction.where}
									/>
								</div>
								<div className='input-field transaction_edit_panel__split-amount'>
									<CurrencyInput
										aria-label='Split amount'
										allowNegative={true}
										name='amount'
										value={split.amount}
										disabled={!!isSaving}
										onChange={(amount) => onSplitAmount(split, amount)}
										onKeyDown={onEnter}
									/>
								</div>
							</li>
						))}
					</ul>
					<p className={`transaction_edit_panel__hint transaction_edit_panel__split-status ${splits.ok ? '' : 'is-error'}`}>
						{splits.text}
					</p>
				</section>
				}

				<section className='transaction_edit_panel__section'>
					<h6>Notes</h6>
					<div className='input-field'>
						<TextInput
							textarea
							aria-label='Notes'
							placeholder='Anything worth remembering'
							name='description'
							value={transaction.description === null || transaction.description === undefined ? '' : transaction.description}
							disabled={!!isSaving}
							onChange={onChange}
							onBlur={onBlur}
							onKeyDown={onEnter}
						/>
					</div>
				</section>
			</div>

			{confirmingDelete &&
			<div className='transaction_edit_panel__confirm' role='alertdialog' aria-label='Delete this transaction'>
				<p>Delete {where || 'this transaction'}? This cannot be undone.</p>
				<Button flat className='transaction_edit_panel__confirm-cancel' onClick={() => setConfirmingDelete(false)}>
					Cancel
				</Button>
				<Button
					flat
					className='transaction_edit_panel__confirm-delete'
					onClick={() => {
						setConfirmingDelete(false);
						onDelete();
					}}
				>
					Delete
				</Button>
			</div>
			}

			<div className='transaction_edit_panel__footer'>
				{!!transaction.submitError &&
				<p className='transaction_edit_panel__status is-error'>Last attempt failed: {transaction.submitError}</p>
				}
				<div className='transaction_edit_panel__actions'>
					{!isNew && !!onDelete &&
					<Button
						flat
						className='transaction_edit_panel__delete'
						disabled={!!isSaving || confirmingDelete}
						onClick={() => setConfirmingDelete(true)}
					>
						<MaterialIcon className='left'>delete</MaterialIcon>
						Delete
					</Button>
					}
					<Button
						flat
						type='submit'
						className='transaction_edit_panel__save'
						disabled={!!isSaving}
						onClick={onSave}
					>
						<MaterialIcon className='left'>check</MaterialIcon>
						{isSaving ? 'Saving...' : (isNew ? 'Add' : 'Save')}
					</Button>
				</div>
			</div>
		</div>
	);
}

function categoryHint(categoryLocked, isSplit) {
	if(categoryLocked) {
		return 'Added from this category, so it stays here.';
	}
	if(isSplit) {
		return 'Split between these. Set each one’s share below.';
	}
	return 'Pick more than one to split the total between them.';
}
